import {
  FluidDependentFieldValidation,
  FluidFieldValidationType,
  FluidFormElementConfig,
  FluidFormElementState,
  FluidFormElementType,
  FluidFormState,
  FluidKeyedObject,
  FluidOption,
  FluidRowData,
  FluidTableHeader,
  FluidTableRowData,
} from '@lmig/fluid-core';
import { forceUpdate } from '@stencil/core';

import {
  ELEMENTS_WITH_SELECTABLE_OPTIONS,
  FORM_EXPANDED,
  ID,
  MULTIPLE_SELECTED_INITIAL,
} from '../../../constants/constants';
import { Utils } from '../../../utils/utils';
import {
  canUseIdentifiers,
  getDataAtPath,
  getMutatedRowData,
  is,
  rowDataToFormState,
} from './fluid-table-data-management.utils';
import { isValidValue } from './fluid-table-select.utils';

/**
 * When an expandable form is rendered, take its reference and store it against the
 * host so we have a list of forms to get value or reset where necessary.
 * @param host
 * @param rowId
 * @param reference
 */
export const addFormReference = (
  host,
  rowId: string,
  reference: HTMLFluidFormElement
) => {
  host._editableForms[rowId] = reference;
  reference?.markTouchedAndValidate();
};

/**
 * Sets the value on the reference, before adding it to the host reference
 * list and adding a focus event listener
 * @param el
 * @param dataItem
 * @param header
 * @param mutatedValue
 * @param addCellReference
 * @param onFocus
 * @param updatedRows
 * @param config
 */
export async function addElementReference(
  el: HTMLFluidFormElementElement,
  dataItem: FluidRowData,
  header: FluidTableHeader,
  mutatedValue: any,
  addCellReference: Function,
  updatedRows: FluidKeyedObject<any>,
  config: Partial<FluidFormElementConfig>,
  onFocus?: Function
) {
  if (el) {
    const valueToSet = gatherInitialValueToSet(
      header,
      mutatedValue,
      dataItem,
      config
    );
    await el.setValue(valueToSet);
    await setParentValue(config, el, updatedRows, dataItem);
    addCellReference(dataItem.rowId, header.dataPath, el);
    if (Utils.isFunction(onFocus)) {
      el.addEventListener('focus', (event) => onFocus(event));
    }
  }
}

/**
 * Sets the parent value on the element to that of the current row data,
 * so it has a reference in case its validation is dependent on the other
 * parts of the data object
 * @param config
 * @param el
 * @param updatedRows
 * @param dataItem
 */
async function setParentValue(config, el, updatedRows, dataItem): Promise<any> {
  if (hasCustomDependentValidation(config)) {
    return el.setParentValue(
      rowDataToFormState(getMutatedRowData(updatedRows, dataItem))
    );
  }
  return Promise.resolve();
}

/**
 * Gathers the initial value to set on an element reference based on cell type
 * @param header
 * @param mutatedValue
 * @param dataItem
 * @param config
 */
export const gatherInitialValueToSet = (
  header: FluidTableHeader,
  mutatedValue: any,
  dataItem: FluidRowData,
  config: Partial<FluidFormElementConfig>
) => {
  const valueToSet = isValidValue(
    mutatedValue,
    (header.editableCellConfig as FluidFormElementConfig).elementType
  )
    ? mutatedValue
    : getDataAtPath(dataItem, header);
  return findInitialValueFromSelectableOptions(
    valueToSet,
    config.elementType,
    config.options
  );
};

/**
 * If element is a selectable option element type, returns the option marked 'selected' if
 * value is given as undefined.
 * @param value
 * @param elementType
 * @param options
 */
export const findInitialValueFromSelectableOptions = (
  value: any,
  elementType: FluidFormElementType,
  options?: FluidOption[]
) => {
  if (!value && elementHasSelectableOptions(elementType)) {
    if (options.filter((opt) => opt.selected).length > 1) {
      Utils.consoleWarn(
        { tagName: elementType.toString() } as HTMLElement,
        MULTIPLE_SELECTED_INITIAL
      );
    }
    return options.find((opt) => opt.selected)?.value;
  }
  return value;
};

/**
 * Checks if the element type uses selectable options.
 * @param elementType
 */
export const elementHasSelectableOptions = (
  elementType: FluidFormElementType
): boolean => {
  return ELEMENTS_WITH_SELECTABLE_OPTIONS.indexOf(elementType) !== -1;
};

/**
 * Called whenever there is a change to one of the form fields in an expandable form.
 * Due to the nature of re-renders and data updates, we only consider a field 'updated'
 * when it is actually touched, so we can keep a track of user interactions, rather than
 * system interactions.
 * @param host
 * @param update
 * @param dataItem
 */
export const handleFormUpdate = (
  host,
  update: FluidFormState,
  dataItem: FluidRowData
) => {
  Object.keys(update.value).forEach((controlName: string) => {
    if (valueHasChanged(update.value[controlName], dataItem)) {
      const elementState = update.value[controlName] as FluidFormElementState;
      host._updatedRows[dataItem.rowId] = {
        ...host._updatedRows[dataItem.rowId],
        [elementState.dataPath]: {
          rowId: dataItem.rowId,
          newValue: elementState.value,
          dataPath: elementState.dataPath,
        },
      };
    }
  });
  if (host.trackFormState) {
    host.updateAndEmitFormState();
  }
};

export const handleValueChange = (
  host,
  update: FluidFormElementState,
  dataItem: FluidRowData
) => {
  host.formValueChanged.emit({
    [ID]: dataItem[ID],
    update,
    rowData: dataItem,
    actionKey: update.name,
  });
};

/**
 * Checks the value of a partial update against the current dataItem value to see
 * if the value has changed - if it has, we can mark it as an updated row.
 * @param elementState
 * @param dataItem
 */
const valueHasChanged = (
  elementState: FluidFormElementState,
  dataItem: FluidRowData
) => {
  return (
    elementState.value != getDataAtPath(dataItem, null, elementState.dataPath)
  );
};

/**
 * Gets references to all the editable forms, and returns the state of each in an array.
 * @param editableForms
 */
export const getEditableFormStates = (
  editableForms: FluidKeyedObject<HTMLFluidFormElement>
): Promise<FluidFormState>[] => {
  return getEditableFormReferences(editableForms).map((ref) =>
    ref?.getFormState()
  );
};

/**
 * Returns an array of form references when given a keyed object of references.
 * @param editableForms
 */
export const getEditableFormReferences = (
  editableForms: FluidKeyedObject<HTMLFluidFormElement>
): HTMLFluidFormElement[] => {
  return Object.keys(editableForms).reduce((refs: any[], key: string) => {
    if (editableForms[key]) {
      refs.push(editableForms[key]);
    }
    return refs;
  }, []);
};

/**
 * Sets the form data for each form reference so that it's up to date with the
 * latest set of dataItems.
 * @param host
 * @param dataItems
 */
export const updateFormData = (host, dataItems: FluidRowData[]) => {
  dataItems.forEach((ds: FluidRowData) => {
    (host._editableForms[ds.rowId] as HTMLFluidFormElement)?.setFormValue(ds);
  });
};

/**
 * Grabs all the form references and calls their exposed reset() method to reset each form
 * to its initial state when rendered.
 * @param host
 */
export const resetEditableForms = (host) => {
  getEditableFormReferences(host._editableForms).forEach((ref) => ref.reset());
  forceUpdate(host);
};

/**
 * Returns true if the form row is expanded.
 */
export const formExpanded = <DataItem extends FluidTableRowData<DataItem>>(
  expandedRows: Record<string, unknown> = {},
  dataItem: DataItem,
  dataSource,
  rowIdentifier?: keyof DataItem
) => {
  return (
    isExpandedById(expandedRows, dataItem, dataSource, rowIdentifier) ||
    is(dataItem, dataSource, FORM_EXPANDED)
  );
};

/**
 * Returns true if the row is expanded by a rowIdentifier
 * @param expandedRows
 * @param dataItem
 * @param dataSource
 * @param rowIdentifier
 */
export const isExpandedById = <DataItem extends FluidTableRowData<DataItem>>(
  expandedRows: Record<string, unknown>,
  dataItem: DataItem,
  dataSource: DataItem[],
  rowIdentifier?: keyof DataItem
) => {
  return (
    expandedRows[dataItem[ID]] && canUseIdentifiers(dataSource, rowIdentifier)
  );
};

/**
 * Returns true if the field has custom validation based on a dependent field (no point setting a parent
 * value with derived state, if there's no dependent validation)
 * @param config
 */
export const hasCustomDependentValidation = (
  config: FluidFormElementConfig
) => {
  return (
    (config.validation || []).filter((v) => {
      return (
        (v.type === FluidFieldValidationType.CUSTOM &&
          !!(v as FluidDependentFieldValidation).dependentField) ||
        v.type === FluidFieldValidationType.DEPENDENT
      );
    }).length > 0
  );
};
